import { useEffect, useState } from 'react';
import { Plus, Trash2 } from '@/components/icons';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { CustomIconPanel } from '../../components/CustomIconPanel';
import { resolveAssetUrl } from '../../lib/assets';
import {
  listCustomIcons,
  addCustomIcon,
  removeCustomIcon,
  type CustomIcon,
} from '../../services/customIconLibrary';

/**
 * "My Nodes" — the user's library of uploaded custom node icons. Uses the same
 * CustomIconPanel as the editor's command palette for uploads (one shared system);
 * this screen adds the library grid + delete. v1 is localStorage-backed (per-device).
 */
export default function MyNodes() {
  const [icons, setIcons] = useState<CustomIcon[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);

  const refresh = async () => {
    setIcons(await listCustomIcons());
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleAdd = async (iconId: string, name?: string) => {
    await addCustomIcon({ url: iconId, name });
    setUploadOpen(false);
    refresh();
  };

  const handleDelete = async (id: string) => {
    await removeCustomIcon(id);
    refresh();
  };

  const addButton = (
    <button
      onClick={() => setUploadOpen(true)}
      className="ss-press inline-flex h-[34px] items-center gap-1.5 rounded-md bg-foreground px-3 text-[12.5px] font-semibold text-background shadow-md transition-opacity hover:opacity-90"
    >
      <Plus className="h-[15px] w-[15px]" />
      Add custom node
    </button>
  );

  return (
    <div className="px-6 pb-16 pt-6 md:px-8">
      <div className="flex flex-col gap-[18px]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[21px] font-semibold tracking-tight">My Nodes</h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              Your uploaded custom node icons — reuse them across diagrams. Stored on this device.
            </p>
          </div>
          {addButton}
        </div>

        {loading ? null : icons.length === 0 ? (
          <div className="rounded-xl border border-border bg-card px-4 py-16 text-center shadow-sm">
            <p className="text-[14px] font-semibold text-foreground">No custom nodes yet</p>
            <p className="mt-1 text-[12.5px] text-muted-foreground">
              Upload an SVG/PNG, paste SVG markup, or add an image URL to reuse it as a node icon.
            </p>
            <div className="mt-4 flex justify-center">{addButton}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {icons.map((ic) => (
              <div
                key={ic.id}
                className="group relative flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="grid h-14 w-14 place-items-center">
                  <img src={resolveAssetUrl(ic.url)} alt={ic.name} className="max-h-12 max-w-12 object-contain" />
                </div>
                <span className="w-full truncate text-center text-[12px] text-muted-foreground">
                  {ic.name}
                </span>
                <button
                  onClick={() => handleDelete(ic.id)}
                  aria-label={`Remove ${ic.name}`}
                  className="ss-press absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-[420px] p-0">
          <DialogTitle className="sr-only">Add a custom node</DialogTitle>
          <CustomIconPanel addLabel="Add to library" onAdd={handleAdd} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
